import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Check, Loader2, AlertCircle, Trash2, Save, XCircle } from 'lucide-react';
import { useAuth, authHeaders } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DISCIPLINE_OPTIONS = [
  'Skeet', 'Skeet Olympique', 'DTL',
  'Fosse Olympique', 'Fosse Américaine',
  'Parcours de Chasse', 'Compak Sporting', 'Helice',
];

const ALLOWED_MIME_PREFIXES = ['image/', 'application/pdf'];

function StatusPill({ status }) {
  const styles = {
    pending: { bg: 'bg-yellow-500/15 border-yellow-500/30 text-yellow-300', label: 'En attente de validation' },
    approved: { bg: 'bg-green-500/15 border-green-500/30 text-green-300', label: 'Validé · publié' },
    rejected: { bg: 'bg-red-500/15 border-red-500/30 text-red-300', label: 'Refusé' },
    cancelled: { bg: 'bg-gray-500/15 border-gray-500/30 text-gray-300', label: 'Annulé' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${s.bg}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {s.label}
    </span>
  );
}

export default function MonEspaceEvent() {
  const { id } = useParams();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [removePoster, setRemovePoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true, state: { from: `/mon-espace/event/${id}` } }); return; }

    let mounted = true;
    axios.get(`${API}/my/country-events/${id}`, { headers: authHeaders(token) })
      .then((r) => {
        if (!mounted) return;
        setEvent(r.data);
        setForm({
          name: r.data.name || '',
          organizer: r.data.organizer || '',
          address: r.data.address || '',
          city: r.data.city || '',
          postal_code: r.data.postal_code || '',
          department: r.data.department || '',
          date_start: r.data.date_start || '',
          date_end: r.data.date_end || '',
          opening_hours: r.data.opening_hours || '',
          disciplines: r.data.disciplines || [],
          price_info: r.data.price_info || '',
          description: r.data.description || '',
          phone: r.data.phone || '',
          email: r.data.email || '',
          website: r.data.website || '',
        });
      })
      .catch((err) => { if (mounted) setNotFound(err.response?.status === 404); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id, user, token, authLoading, navigate]);

  function update(key, value) { setForm((f) => ({ ...f, [key]: value })); }

  function toggleDiscipline(d) {
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(d) ? f.disciplines.filter((x) => x !== d) : [...f.disciplines, d],
    }));
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    setError('');
    if (!file) { setPosterFile(null); setPosterPreview(null); return; }
    if (!ALLOWED_MIME_PREFIXES.some((p) => file.type.startsWith(p))) {
      setError('Format non supporté. Utilisez une image ou un PDF.'); return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 15 Mo).'); return;
    }
    setPosterFile(file);
    setRemovePoster(false);
    if (file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif') {
      const reader = new FileReader();
      reader.onload = (ev) => setPosterPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPosterPreview(null);
    }
  }

  async function save(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.name.trim() || form.name.trim().length < 3) {
      setError("Le nom de l'évènement est requis."); return;
    }
    if (!form.postal_code.trim() && !form.department.trim()) {
      setError('Code postal ou département requis.'); return;
    }
    setSaving(true);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'disciplines') data.append(k, v.join(','));
      else data.append(k, v);
    });
    if (posterFile) data.append('poster', posterFile);
    if (removePoster) data.append('remove_poster', 'true');
    try {
      const r = await axios.put(`${API}/my/country-events/${id}`, data, {
        headers: { ...authHeaders(token), 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(r.data?.message || 'Modifications enregistrées.');
      const fresh = await axios.get(`${API}/my/country-events/${id}`, { headers: authHeaders(token) });
      setEvent(fresh.data);
      setPosterFile(null); setPosterPreview(null); setRemovePoster(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }

  async function cancelEvent() {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler définitivement votre évènement ? Il ne sera plus visible publiquement.')) return;
    setCancelling(true); setError(''); setSuccess('');
    try {
      const r = await axios.post(`${API}/my/country-events/${id}/cancel`, {}, { headers: authHeaders(token) });
      setSuccess(r.data?.message || 'Évènement annulé.');
      const fresh = await axios.get(`${API}/my/country-events/${id}`, { headers: authHeaders(token) });
      setEvent(fresh.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue.');
    } finally { setCancelling(false); }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center font-['Manrope']">
        <div className="flex items-center gap-3 text-[#888]">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
        </div>
      </div>
    );
  }
  if (!user) return null;
  if (notFound || !event || !form) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center font-['Manrope'] px-4">
        <div className="max-w-md w-full text-center rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-red-500/20 p-8">
          <div className="inline-flex w-16 h-16 rounded-full bg-red-500/15 text-red-300 items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h1 className="text-xl font-black mb-2">Évènement introuvable</h1>
          <p className="text-sm text-[#C8C8C8] mb-6">
            Cet évènement n'existe plus ou ne vous appartient pas.
          </p>
          <Link to="/mon-espace" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FFC107] text-black font-bold text-sm">
            Retour à mon espace
          </Link>
        </div>
      </div>
    );
  }

  const isCancelled = event.status === 'cancelled';
  const posterUrl = event.poster_url ? `${API.replace('/api','')}${event.poster_url}` : null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <div className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.12), transparent 50%)' }} />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/mon-espace" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Mon espace
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
          <h1 className="text-3xl sm:text-4xl font-black">Modifier mon évènement</h1>
          <StatusPill status={event.status} />
        </div>
        <p className="text-[#C8C8C8] mb-6 leading-relaxed text-sm">
          Vous pouvez modifier ou annuler votre ball-trap. Toute modification entraîne une nouvelle validation par l'administrateur.
        </p>

        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-500/15 border border-green-500/30 text-green-200 flex items-start gap-3" data-testid="manage-success">
            <Check className="w-5 h-5 flex-shrink-0 mt-0.5" /><p className="text-sm">{success}</p>
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 flex items-start gap-3" data-testid="manage-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /><p className="text-sm">{error}</p>
          </div>
        )}

        {event.status === 'rejected' && event.rejection_reason && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300/90">
            <p className="text-xs font-bold uppercase tracking-widest mb-1">Raison du refus</p>
            <p className="text-sm">{event.rejection_reason}</p>
          </div>
        )}

        {isCancelled ? (
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-6 text-center">
            <p className="text-[#C8C8C8]">
              Cet évènement a été <strong>annulé</strong> et n'apparaît plus publiquement.
              Si vous souhaitez le republier, créez une nouvelle déclaration.
            </p>
            <Link to="/ball-trap-campagne/soumettre" className="inline-flex mt-4 items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFC107] text-black font-bold text-sm">
              Déclarer un nouvel évènement
            </Link>
          </div>
        ) : (
          <form onSubmit={save} className="space-y-6" data-testid="manage-form">
            <Field label="Nom de l'évènement *">
              <input required value={form.name} onChange={(e) => update('name', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                data-testid="mfield-name" />
            </Field>
            <Field label="Club ou organisateur">
              <input value={form.organizer} onChange={(e) => update('organizer', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                data-testid="mfield-organizer" />
            </Field>
            <Field label="Description">
              <textarea rows={4} value={form.description} onChange={(e) => update('description', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none resize-y"
                data-testid="mfield-description" />
            </Field>
            <Field label="Adresse">
              <input value={form.address} onChange={(e) => update('address', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                data-testid="mfield-address" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code postal *">
                <input value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)}
                  maxLength={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  data-testid="mfield-postal-code" />
              </Field>
              <Field label="Ville">
                <input value={form.city} onChange={(e) => update('city', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  data-testid="mfield-city" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date de début">
                <input type="date" value={form.date_start} onChange={(e) => update('date_start', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
              </Field>
              <Field label="Date de fin">
                <input type="date" value={form.date_end} onChange={(e) => update('date_end', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
              </Field>
            </div>
            <Field label="Horaires">
              <input value={form.opening_hours} onChange={(e) => update('opening_hours', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
            <Field label="Tarifs">
              <input value={form.price_info} onChange={(e) => update('price_info', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>

            <fieldset>
              <legend className="text-xs font-semibold text-[#C8C8C8] mb-2">Disciplines proposées</legend>
              <div className="flex flex-wrap gap-2">
                {DISCIPLINE_OPTIONS.map((d) => {
                  const active = form.disciplines.includes(d);
                  return (
                    <button type="button" key={d} onClick={() => toggleDiscipline(d)}
                      className={`px-3.5 py-2 text-sm rounded-full border transition-all ${active
                        ? 'bg-[#FFC107] border-[#FFC107] text-black font-bold'
                        : 'bg-white/5 border-white/10 text-[#C8C8C8] hover:border-[#FFC107]/40'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Téléphone">
                <input value={form.phone} onChange={(e) => update('phone', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
              </Field>
              <Field label="Email public">
                <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
              </Field>
            </div>
            <Field label="Site web ou Facebook">
              <input type="url" value={form.website} onChange={(e) => update('website', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>

            <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
              <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">Affiche</legend>
              {posterUrl && !removePoster && !posterFile && (
                <div className="mb-3 flex items-center gap-3 p-3 rounded-lg bg-black/30 border border-white/10">
                  {event.poster_mime?.includes('pdf') ? (
                    <div className="w-16 h-20 flex items-center justify-center rounded bg-[#FFC107]/10 text-[#FFC107] text-xs font-bold">PDF</div>
                  ) : (
                    <img src={posterUrl} alt="Affiche actuelle" className="w-16 h-20 object-cover rounded" />
                  )}
                  <div className="flex-1 text-xs text-[#C8C8C8]">Affiche actuelle</div>
                  <button type="button" onClick={() => setRemovePoster(true)} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200">
                    <Trash2 className="w-3.5 h-3.5" /> Retirer
                  </button>
                </div>
              )}
              {removePoster && (
                <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs flex items-center justify-between">
                  L'affiche actuelle sera supprimée à l'enregistrement.
                  <button type="button" onClick={() => setRemovePoster(false)} className="text-red-200 hover:text-white text-xs underline">Annuler</button>
                </div>
              )}
              <label className="block cursor-pointer">
                <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" />
                <div className="border-2 border-dashed border-white/15 rounded-xl p-5 hover:border-[#FFC107]/50 transition-colors text-center">
                  {posterPreview ? (
                    <>
                      <img src={posterPreview} alt="Aperçu" className="max-h-48 mx-auto rounded-lg shadow-lg mb-2" />
                      <p className="text-xs text-[#888]">Cliquez pour changer</p>
                    </>
                  ) : posterFile ? (
                    <p className="text-sm text-white">{posterFile.name}</p>
                  ) : (
                    <>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/5 text-white/60 mb-2">
                        <Upload className="w-5 h-5" />
                      </div>
                      <p className="text-sm text-white">Remplacer l'affiche</p>
                      <p className="text-xs text-[#888] mt-1">Images & PDF · 15 Mo max</p>
                    </>
                  )}
                </div>
              </label>
            </fieldset>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={cancelEvent}
                disabled={cancelling}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors text-sm disabled:opacity-50"
                data-testid="cancel-event-btn"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Annuler mon évènement
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-60 hover:scale-[1.03] transition-transform"
                data-testid="save-event-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#C8C8C8] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
